import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { MapPin, Clock, Shield, Users, ArrowRight, Sparkles, Bus } from "lucide-react";
import { Footer } from "@/components/Footer";
import { HUBSTOPS } from "@/lib/utils";

const features = [
  {
    icon: MapPin,
    title: "Custom Routes",
    description:
      "Get personalized routes from your doorstep to school or summer camp. Our smart routing ensures efficient, safe transportation.",
  },
  {
    icon: Clock,
    title: "Flexible Scheduling",
    description:
      "Choose from pre-defined hubstop pickups or request custom times. We adapt to your family's schedule.",
  },
  {
    icon: Shield,
    title: "Safe & Vetted Drivers",
    description:
      "Every driver undergoes thorough background checks and training. Your child's safety is our top priority.",
  },
  {
    icon: Users,
    title: "Small Group Transport",
    description:
      "Maximum 6 students per vehicle ensures personal attention and a comfortable ride for every child.",
  },
];


export default function Home() {
  return (
    <div className="flex flex-col">
      {/* Hero Section with Enhanced Design */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-900 via-blue-700 to-blue-600 pt-20 pb-32">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-300 rounded-full blur-3xl" style={{ animationDelay: '1s' }} />
        </div>

        <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
          <div className="mx-auto max-w-4xl text-center">
            <div className="animate-fade-in-up">
              <a
                href="https://www.sch.org/summerside-camps"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-sm px-4 py-2 text-sm font-semibold text-white mb-8 border border-white/20 hover:bg-white/20 transition-all duration-300"
              >
                <Sparkles className="w-4 h-4" />
                Summer 2026 Registration Now Open
              </a>

              <h1 className="font-display text-5xl font-bold tracking-tight text-white sm:text-7xl mb-6">
                Summerside Camps at SCH
              </h1>

              <p className="text-3xl font-bold text-blue-100 mb-4">Safe & Reliable Student Transportation</p>
              <p className="text-xl text-blue-200 max-w-2xl mx-auto mb-10 leading-relaxed">
                Let's get your students moving—together. We make it easy for parents to book safe, reliable transportation for their children.
              </p>

              <div className="flex items-center justify-center gap-4 flex-wrap">
                <Link to="/summer-camp-quote">
                  <Button size="xl" className="bg-white text-blue-600 hover:bg-blue-50 shadow-2xl hover:shadow-white/20 hover:-translate-y-1 transition-all duration-300 text-lg px-8 py-6">
                    Summerside Camps at SCH Reservation Request
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/shuttle-info">
                  <Button size="xl" className="bg-blue-500/30 backdrop-blur-sm text-white border-2 border-white/30 hover:bg-blue-500/50 hover:border-white/50 shadow-2xl hover:-translate-y-1 transition-all duration-300 text-lg px-8 py-6">
                    Everything You Need To Know
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-24 grid grid-cols-2 gap-8 md:grid-cols-4 max-w-5xl mx-auto">
            {[
              { value: "500+", label: "Families Served" },
              { value: "10K+", label: "Safe Rides" },
              { value: "98%", label: "On-Time Rate" },
              { value: "4.9★", label: "Parent Rating" },
            ].map((stat) => (
              <div key={stat.label} className="text-center group">
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all duration-300 hover-lift">
                  <div className="font-display text-5xl font-bold text-white mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-blue-200 font-medium">
                    {stat.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl mb-6">
              Why Families Choose Shuttlebee
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-blue-400 mx-auto rounded-full mb-6"></div>
            <p className="text-xl text-gray-600">
              Transportation designed around your family's needs
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div key={feature.title} className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-blue-400 rounded-2xl blur opacity-20 group-hover:opacity-30 transition duration-300"></div>
                  <Card className="relative bg-white border-2 hover:border-blue-300 transition-all duration-300 hover-lift h-full">
                    <CardContent className="pt-8 pb-8">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center mb-6 shadow-lg">
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="font-display font-bold text-xl mb-3 text-gray-900">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Hubstops Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-blue-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>

        <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl font-bold tracking-tight sm:text-5xl mb-4">
              Summerside Camps at SCH Hubstops
            </h2>
            <div className="w-20 h-1 bg-white/30 mx-auto rounded-full mb-6"></div>
            <p className="text-xl text-blue-100">
              Convenient pickup locations across the city
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
            {HUBSTOPS.map((hubstop) => (
              <Link
                key={hubstop.id}
                to={`/hubstop-quote?hubstop=${hubstop.id}`}
                className="group block"
              >
                <div className="bg-white/5 backdrop-blur-sm border-2 border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-white/20 transition-all duration-300 hover-lift h-full">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/30 flex items-center justify-center group-hover:bg-blue-500/50 transition-colors">
                      <Bus className="w-6 h-6 text-blue-200" />
                    </div>
                    <h3 className="font-semibold text-lg text-white">{hubstop.name}</h3>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-white">
                      ${hubstop.pricePerWeek}
                    </span>
                    <span className="text-blue-200">/week per camper, roundtrip</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-16 text-center">
            <p className="text-blue-100 mb-6 text-lg">
              Don't see your neighborhood? Request a custom route.
            </p>
            <Link to="/custom-quote">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 shadow-2xl hover:shadow-white/20 hover:-translate-y-1 transition-all duration-300">
                Request Custom Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-blue-900 via-blue-700 to-blue-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-300 rounded-full blur-3xl" style={{ animationDelay: '1s' }} />
        </div>

        <div className="mx-auto max-w-4xl px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-display text-4xl md:text-5xl font-bold mb-6">
            Ready to simplify your mornings?
          </h2>
          <p className="text-2xl mb-10 text-blue-100">
            Join hundreds of families who trust Shuttlebee for safe, reliable
            transportation. Get your personalized quote today.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/summer-camp-quote">
              <Button
                size="xl"
                className="bg-white text-blue-600 hover:bg-blue-50 shadow-2xl hover:shadow-white/20 hover:-translate-y-1 transition-all duration-300 text-lg px-10 py-6"
              >
                Get Started Today
                <ArrowRight className="ml-2 w-6 h-6" />
              </Button>
            </Link>
            <Link to="/routes">
              <Button
                size="xl"
                className="bg-blue-500/30 backdrop-blur-sm text-white border-2 border-white/30 hover:bg-blue-500/50 hover:border-white/50 shadow-2xl hover:-translate-y-1 transition-all duration-300 text-lg px-10 py-6"
              >
                View All Routes
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
